import { HttpErrorResponse } from "@angular/common/http";
import { Component, ElementRef, OnInit, inject, viewChild } from "@angular/core";
import { ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { MutexService } from "common/concurrency/mutex.service";
import { Button } from "common/data/data-view.component";
import { FormService } from "common/form/form.service";
import { NewDialogComponent } from "common/newdialog/dialog.component";
import { FileService } from "files/files.service";
import { EMPTY, Observable, Subject } from "rxjs";
import { catchError } from "rxjs/operators";
import { DialogHeaderComponent } from "common/newdialog/dialog-header.component";
import { TranslateModule } from "@ngx-translate/core";
import { AsyncPipe } from "@angular/common";
import { EditAttributeComponent } from "common/attribute/edit-attribute.component";
import { PopupContentComponent } from "common/popup/popup-content.component";
import { FilesLoadingErrorEmbeddableComponent } from "files/error/stages-loading-error-embeddable.component";
import { PopupFooterComponent } from "common/popup/popup-footer.component";

type Attribute = stages.core.Attribute;
type AttributeType = stages.core.AttributeType;

@Component({
	selector: "stages-files-revert",
	templateUrl: "./files-revert.component.html",
	imports: [
		NewDialogComponent,
		DialogHeaderComponent,
		TranslateModule,
		ReactiveFormsModule,
		EditAttributeComponent,
		PopupContentComponent,
		FilesLoadingErrorEmbeddableComponent,
		PopupFooterComponent,
		AsyncPipe,
	],
})
export class FilesRevertComponent implements OnInit {
	private route = inject(ActivatedRoute);
	private fileService = inject(FileService);
	private formService = inject(FormService);
	private mutexService = inject(MutexService);

	revertDataAndInfo$: Observable<DataAndInfo<Attribute[], AttributeType[]>>;
	formModel: UntypedFormGroup;
	loadingError$ = new Subject<HttpErrorResponse>();

	readonly dialog = viewChild.required<NewDialogComponent>("dialog");

	readonly formElement = viewChild.required<ElementRef>("formElement");

	buttons: Button[] = [];

	constructor() {
		this.revertDataAndInfo$ = this.fileService
			.getRevertDataAndInfo(
				this.route.snapshot.paramMap.get("fileId")!,
				this.route.snapshot.paramMap.get("workspaceId")!,
				this.route.snapshot.paramMap.get("processVersion")!,
			)
			.pipe(
				catchError((e: unknown) => {
					if (e instanceof HttpErrorResponse) {
						this.loadingError$.next(e);
						return EMPTY;
					}
					throw e;
				}),
			);

		this.formModel = new UntypedFormGroup({});
	}

	ngOnInit(): void {
		this.buttons = [
			{
				class: "button sm cancel",
				translate: "cancel",
				click: () => {
					this.close();
				},
				visible: () => true,
				disabled: () => {
					return false;
				},
			},
		];
	}

	submit(attributes: Attribute[]): void {
		this.formService.markFormGroupTouched(this.formModel);
		if (this.formModel.invalid) {
			this.formService.scrollToFirstInvalidElement(this.formElement());
		} else {
			this.mutexService.invoke("revert" + this.route.snapshot.paramMap.get("fileId")!, async () => {
				const outgoingAttributes = attributes.map((attribute) => {
					const attributeFormControl = this.formModel.controls[attribute.typeIdent];
					attribute.value = attributeFormControl ? attributeFormControl.value : null;
					return attribute;
				});

				return this.fileService
					.doUnlock(
						this.route,
						this.route.snapshot.paramMap.get("fileId")!,
						outgoingAttributes,
						this.route.snapshot.paramMap.get("workspaceId")!,
						this.route.snapshot.paramMap.get("processVersion")!,
					)
					.then(() => {
						this.dialog().close();
					}, this.formService.setServerSideErrorsOnFormControls(this.formModel.controls));
			});
		}
	}

	close = (): void => {
		const dialog = this.dialog();
		if (dialog) {
			dialog.close();
		}
	};
}
